'use client'

import { useMemo, useRef } from "react"
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import {
    arrayMove,
    SortableContext,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import MiniAssetPicker, { SortableMiniAssetPicker } from "./MiniAssetPicker"
import { pickerDedupeKey, dedupeAgainst } from "./pickerValues"

/**
 * dnd-kit modifier that strips the y-axis from drag transforms so sortable
 * items in a horizontal row don't lift vertically while dragging.
 */
const restrictToHorizontalAxis = ({ transform }) => ({
    ...transform,
    y: 0,
})

/**
 * Clamps a {x, y} translation so the supplied node rect stays inside the
 * supplied bounding rect. Shared helper used by the parent-element and
 * window-edges restrict modifiers.
 */
function clampTransformToRect(transform, nodeRect, boundingRect) {
    if (!nodeRect || !boundingRect) return transform
    const next = { ...transform }
    if (nodeRect.left + transform.x <= boundingRect.left) {
        next.x = boundingRect.left - nodeRect.left
    } else if (nodeRect.right + transform.x >= boundingRect.right) {
        next.x = boundingRect.right - nodeRect.right
    }
    if (nodeRect.top + transform.y <= boundingRect.top) {
        next.y = boundingRect.top - nodeRect.top
    } else if (nodeRect.bottom + transform.y >= boundingRect.bottom) {
        next.y = boundingRect.bottom - nodeRect.bottom
    }
    return next
}

/** dnd-kit modifier preventing the dragging node from leaving the viewport. */
const restrictToWindowEdges = ({ draggingNodeRect, transform, windowRect }) =>
    clampTransformToRect(transform, draggingNodeRect, windowRect)

/**
 * Returns a dnd-kit modifier that prevents the dragging node from leaving the
 * rect of the element pointed to by `containerRef`. We compute the rect lazily
 * on each call so layout changes (resize, scroll) are picked up.
 */
function makeRestrictToContainer(containerRef) {
    return ({ draggingNodeRect, transform }) => {
        const el = containerRef.current
        if (!el || !draggingNodeRect) return transform
        return clampTransformToRect(transform, draggingNodeRect, el.getBoundingClientRect())
    }
}
/**
 * Renders a horizontal sortable row of MiniAssetPicker items for an elements array,
 * plus a non-sortable "add" picker at the end (when under max).
 *
 * `prop` is the model input's prop name — it only names the row's dom ids, which a
 * model with two elements inputs would otherwise collide on.
 */
function ElementsRow({ prop, values, mediaType, max, onChange, onPreview, resolveAssetById, onAssetDrop }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
    const rowRef = useRef(null)
    const restrictToRow = useMemo(() => makeRestrictToContainer(rowRef), [])
    const modifiers = useMemo(
        () => [restrictToHorizontalAxis, restrictToRow, restrictToWindowEdges],
        [restrictToRow],
    )

    // Stable per-render ids: encode position + identity so duplicates don't
    // collide in a single render. Pending uploads use their previewUrl; rich
    // values use their canonical url; plain strings use themselves.
    const items = useMemo(() => values.map((v, idx) => {
        const key = typeof v === 'string'
            ? v
            : (v?.previewUrl || v?.url || '')
        return { id: `${idx}::${key}`, value: v }
    }), [values])
    const ids = useMemo(() => items.map(i => i.id), [items])

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        const oldIndex = ids.indexOf(active.id)
        const newIndex = ids.indexOf(over.id)
        if (oldIndex === -1 || newIndex === -1) return
        onChange(arrayMove(values, oldIndex, newIndex))
    }

    return (
        <div id={`ElementsRow--${prop}`} className='flex items-center gap-2 px-[1px]'>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={modifiers}
            >
                <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
                    {/* `rowRef` wraps only the sortable items so the drag bounds
                        stop at the last existing picker and exclude the trailing
                        "+" add picker. Inner flex preserves the spacing between
                        the items themselves. */}
                    {values.length > 0 && <div ref={rowRef} id={`ElementsRow__items--${prop}`} className='flex items-center gap-2'>
                        {items.map((item, idx) => (
                            <div key={item.id} id={`ElementsRow__item--${prop}-${idx}`} className='group relative'>
                                <SortableMiniAssetPicker
                                    id={item.id}
                                    asset={item.value}
                                    mediaType={mediaType}
                                    resolveAssetById={resolveAssetById}
                                    onAssetDrop={onAssetDrop}
                                    onPreview={onPreview}
                                    onChange={(newValue) => {
                                        if (newValue == null) {
                                            onChange(values.filter((_, i) => i !== idx))
                                            return
                                        }
                                        // Drop-replace at this index, then collapse any
                                        // resulting duplicates (keep first occurrence) so the
                                        // same asset can't appear twice in the row.
                                        const replaced = values.map((u, i) => i === idx ? newValue : u)
                                        const seen = new Set()
                                        const deduped = replaced.filter(v => {
                                            const k = pickerDedupeKey(v)
                                            if (k == null) return true
                                            if (seen.has(k)) return false
                                            seen.add(k)
                                            return true
                                        })
                                        onChange(deduped)
                                    }}
                                />
                            </div>
                        ))}
                    </div>}
                </SortableContext>
            </DndContext>
            {values.length < max && (
                <div id={`ElementsRow__add--${prop}`} className='group relative'>
                    <MiniAssetPicker
                        asset={null}
                        mediaType={mediaType}
                        resolveAssetById={resolveAssetById}
                        onAssetDrop={onAssetDrop}
                        multiSelect
                        onAdd={(newValues) => {
                            if (!newValues?.length) return
                            const deduped = dedupeAgainst(values, newValues)
                            if (deduped.length === 0) return
                            const remaining = max - values.length
                            onChange([...values, ...deduped.slice(0, remaining)])
                        }}
                        onChange={(newValue) => {
                            if (!newValue) return
                            const deduped = dedupeAgainst(values, [newValue])
                            if (deduped.length === 0) return
                            onChange([...values, deduped[0]])
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default ElementsRow
